import { Card } from '../Models/Card'
import { CardsState } from '../Models/CardsState'

/**
 * Check if a card can be legally placed on a foundation pile.
 *
 * Rules:
 * - An Ace (numValue 1) can go on an empty foundation.
 * - Otherwise, the card must be the same suit (type + color) and exactly
 *   one higher in value than the current top card.
 */
export function canPlaceOnFoundation(card: Card, foundationPile: Card[]): boolean {
    if (foundationPile.length === 0) {
        return card.numValue === 1
    }

    const top = foundationPile[foundationPile.length - 1]
    return (
        !top.isFaceDown &&
        card.type === top.type &&
        card.color === top.color &&
        card.numValue === top.numValue + 1
    )
}

/**
 * Check if a card can be legally placed on a tableau pile.
 *
 * Rules:
 * - A King (numValue 13) can go on an empty tableau.
 * - Otherwise, the card must be the opposite color and exactly one lower
 *   in value than the current top card, which must be face-up.
 */
export function canPlaceOnTableau(card: Card, tableauPile: Card[]): boolean {
    if (tableauPile.length === 0) {
        return card.numValue === 13
    }

    const top = tableauPile[tableauPile.length - 1]
    return !top.isFaceDown && card.color !== top.color && card.numValue === top.numValue - 1
}

/**
 * Collect all cards from a CardsState into a flat array.
 */
export function getAllCards(state: CardsState): Card[] {
    return [...state.stock, ...state.waste, ...state.tableau.flat(), ...state.foundation.flat()]
}

/**
 * Resolve the effective waste visible count, falling back to the provided default if undefined.
 */
export function getWasteVisibleCount(state: Pick<CardsState, 'wasteVisibleCount'>, fallback: number): number {
    return state.wasteVisibleCount !== undefined ? state.wasteVisibleCount : fallback
}

/**
 * Decrement wasteVisibleCount by 1 after removing a card from the waste pile.
 * Clamps to at least 1 when the waste still has cards, or 0 when empty.
 * Mutates state in-place (intended for use inside commitMove callbacks).
 */
export function decrementWasteVisible(state: CardsState, fallback: number): void {
    const current = getWasteVisibleCount(state, fallback)
    state.wasteVisibleCount = state.waste.length > 0 ? Math.max(1, current - 1) : 0
}
